// Acompanhamento do planejamento (Gestão › Orçamentário): orçado do cenário e metas × realizado.
//
// Tudo no sinal do DRE (entrada +, saída −), em que "maior é melhor" vale sempre: mais receita, menos
// despesa (despesa menos negativa) e mais resultado. A única exceção é o que é medido em valor absoluto
// com "menor é melhor" (Saídas realizadas, Ponto de Equilíbrio).
//
// Mês em andamento: o esperado até hoje é o orçado/meta proporcional aos dias passados. A projeção do
// fechamento segue o mesmo ritmo nas entradas (realizado ÷ fração do período); nas saídas, que caem em
// datas certas (aluguel, folha), vale o maior entre o realizado e o orçado do mês.
//
// Período de vários meses (trimestre, últimos 6 meses...): cada mês entra com a própria fração e as
// somas são do período — mês fechado pelo realizado, mês em andamento pela regra acima, mês futuro
// pelo orçado.

import { computeStatement, rankingLines, resultSeries } from './simulate';
import {
  computeBaseIndicatorSeries,
  deriveIndicators,
  INDICATOR_DEF_BY_KEY,
  INDICATOR_DEFS,
  isIndicatorAvailable,
  sumBaseIndicators,
} from './indicators';
import { MONTH_INDEXES, zeros12 } from './periods';
import { METRIC_TYPE } from './goalSnapshot';
import { parseCategoryNature } from './erpCategories';

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const sum = (arr) => arr.reduce((acc, v) => acc + (Number(v) || 0), 0);

// ---------------- Datas ----------------

const parseMonth = (key) => {
  const m = String(key || '').match(/^(\d{4})-(\d{2})/);
  return m ? { year: Number(m[1]), month: Number(m[2]) } : null;
};
const daysInMonth = (year, month) => new Date(year, month, 0).getDate();
const toDate = (d) => (d instanceof Date ? new Date(d.getFullYear(), d.getMonth(), d.getDate()) : new Date(d));
const dayDiff = (a, b) => Math.round((toDate(b) - toDate(a)) / 86400000);

const monthKeyOf = (year, monthIndex) => `${year}-${String(monthIndex + 1).padStart(2, '0')}`;

/**
 * Situação de um período (yyyy-MM..yyyy-MM) em relação a hoje: cada mês com dias passados e fração
 * (mês passado = 1, futuro = 0), os totais de dias e a fração do período.
 * `until` (= `year`/`monthIndex`) é o mês de referência, o último com realizado: o de hoje num período
 * em andamento; senão o último mês do período.
 */
export function periodProgress(startKey, endKey = startKey, today = new Date()) {
  const list = monthsBetween(startKey, endKey);
  if (!list.length) return null;
  const t = toDate(today);
  const offsetOf = (year, monthIndex) => year * 12 + monthIndex;
  const todayOffset = offsetOf(t.getFullYear(), t.getMonth());
  const months = list.map(({ year, monthIndex }) => {
    const days = daysInMonth(year, monthIndex + 1);
    const o = offsetOf(year, monthIndex);
    const elapsed = o < todayOffset ? days : o > todayOffset ? 0 : t.getDate();
    return { year, monthIndex, days, elapsed, fraction: elapsed / days };
  });
  const first = months[0];
  const last = months[months.length - 1];
  let state = 'current';
  if (todayOffset > offsetOf(last.year, last.monthIndex)) state = 'past';
  if (todayOffset < offsetOf(first.year, first.monthIndex)) state = 'future';
  const until = state === 'current' ? { year: t.getFullYear(), monthIndex: t.getMonth() } : { year: last.year, monthIndex: last.monthIndex };
  const days = sum(months.map((p) => p.days));
  const elapsed = sum(months.map((p) => p.elapsed));
  return {
    start: monthKeyOf(first.year, first.monthIndex),
    end: monthKeyOf(last.year, last.monthIndex),
    months,
    single: months.length === 1,
    until,
    year: until.year,
    month: until.monthIndex + 1,
    monthIndex: until.monthIndex,
    days,
    elapsed,
    fraction: days ? elapsed / days : 0,
    state,
  };
}

/** Situação de um mês em relação a hoje (período de um mês só). */
export const monthProgress = (monthKey, today = new Date()) => periodProgress(monthKey, monthKey, today);

/** Data de corte do acompanhamento: hoje no período em andamento; senão o último dia do período (ou o primeiro, se futuro). */
export const asOfDate = (progress) => {
  if (progress.state === 'current') return new Date();
  const months = progress.months || [progress];
  const p = progress.state === 'future' ? months[0] : months[months.length - 1];
  return new Date(p.year, p.monthIndex, progress.state === 'future' ? 1 : p.days);
};

/** Meses (yyyy-MM) entre dois meses, inclusivo. */
export const monthsBetween = (startKey, endKey) => {
  const a = parseMonth(startKey);
  const b = parseMonth(endKey || startKey);
  if (!a || !b) return [];
  const out = [];
  for (let o = a.year * 12 + a.month - 1; o <= b.year * 12 + b.month - 1; o += 1) {
    out.push({ year: Math.floor(o / 12), monthIndex: o % 12 });
  }
  return out;
};

/** Fração já transcorrida de um período (yyyy-MM..yyyy-MM) na data de corte, pelos dias. */
export function periodFraction(startKey, endKey, asOf) {
  const a = parseMonth(startKey);
  const b = parseMonth(endKey || startKey);
  if (!a || !b) return 0;
  const start = new Date(a.year, a.month - 1, 1);
  const end = new Date(b.year, b.month - 1, daysInMonth(b.year, b.month));
  const total = dayDiff(start, end) + 1;
  const elapsed = Math.min(total, Math.max(0, dayDiff(start, asOf) + 1));
  return total > 0 ? elapsed / total : 0;
}

// ---------------- Séries ----------------

const deptKeyOf = (v) => String(v?.codDepartamento ?? '').trim() || null;

/**
 * Realizado por categoria x mês de um ano a partir das linhas do dataset ({ codCategoria, ano, mes,
 * valor, codDepartamento }). `department`: undefined = todos (soma); string = só aquele departamento.
 */
export function realizedSeriesByYear(values, year, { department, until } = {}) {
  const out = new Map();
  (Array.isArray(values) ? values : []).forEach((v) => {
    if (Number(v?.ano) !== year) return;
    const m = Number(v.mes) - 1;
    if (m < 0 || m > 11) return;
    if (until && (year > until.year || (year === until.year && m > until.monthIndex))) return;
    if (department !== undefined && deptKeyOf(v) !== department) return;
    const cod = String(v.codCategoria ?? '').trim();
    if (!cod) return;
    if (!out.has(cod)) out.set(cod, zeros12());
    out.get(cod)[m] += Number(v.valor) || 0;
  });
  return out;
}

/**
 * Orçado (projetado gravado do cenário) por categoria x mês. `department`: null = consolidado
 * (as linhas sem departamento); string = as linhas daquele departamento.
 */
export function budgetSeries(scenarioValues, { department = null } = {}) {
  const out = new Map();
  (Array.isArray(scenarioValues) ? scenarioValues : []).forEach((row) => {
    if ((deptKeyOf(row) || null) !== department) return;
    const cod = String(row?.codCategoria ?? '').trim();
    const months = Array.isArray(row?.monthlyValues) ? row.monthlyValues : [];
    if (!cod || months.length !== 12) return;
    const target = out.get(cod) || zeros12();
    months.forEach((v, m) => { target[m] += Number(v) || 0; });
    out.set(cod, target);
  });
  return out;
}

/** Departamentos que têm orçado gravado (cenário por departamento). */
export const budgetDepartments = (scenarioValues) => [...new Set((scenarioValues || []).map(deptKeyOf).filter(Boolean))];

// ---------------- Orçado × realizado ----------------

/**
 * Esperado até a data de corte, desvio e projeção de uma linha num período, somando os meses
 * (`parts`: [{ budget, realized, fraction }]). `expense`: no mês em andamento a projeção é o maior
 * gasto entre o realizado e o orçado (no sinal do DRE, o menor dos dois), não o ritmo dos dias.
 */
export function comparePeriod(parts, { expense = false } = {}) {
  let budget = 0;
  let realized = 0;
  let expected = 0;
  let projection = 0;
  parts.forEach(({ budget: b, realized: r, fraction }) => {
    budget += b;
    realized += r;
    expected += b * fraction;
    if (fraction >= 1) projection += r;
    else if (fraction <= 0) projection += b;
    else projection += expense ? Math.min(r, b) : r / fraction;
  });
  return {
    budget: round2(budget),
    realized: round2(realized),
    expected: round2(expected),
    projection: round2(projection),
    deviation: round2(realized - expected), // > 0 = melhor que o esperado (sinal do DRE)
    executionPct: budget ? (realized / budget) * 100 : null,
  };
}

/** Esperado até a data de corte, desvio e projeção de uma linha no mês (ver comparePeriod). */
export const compareMonth = (budget, realized, fraction, options) => comparePeriod([{ budget, realized, fraction }], options);

/**
 * Natureza de cada categoria pela própria categoria (cadastro do ERP, ver `categoryNatureLookup`).
 * Só quando o ERP não informa (ex.: categorias calculadas do DRE), pelo sinal: o do orçado no ano,
 * senão o do realizado.
 */
export const categoryNatureLookup = (categoryTree) => {
  const map = new Map();
  (Array.isArray(categoryTree) ? categoryTree : []).forEach((c) => {
    const nature = parseCategoryNature(c?.natureza);
    const cod = String(c?.codCategoria ?? '').trim().toLowerCase();
    if (cod && nature) map.set(cod, nature);
  });
  return (cod) => map.get(String(cod ?? '').trim().toLowerCase()) || null;
};

const natureBySign = (budget, realized) => {
  const b = sum(budget || []);
  if (Math.abs(b) > 0.005) return b > 0 ? 'R' : 'P';
  return sum(realized || []) >= 0 ? 'R' : 'P';
};

/**
 * Orçado × realizado do período e do ano de referência: totais (entradas, saídas, resultado), linhas por
 * categoria e a série mensal do ano de referência. `month` de cada categoria (e dos totais) é a soma do
 * período — que é o próprio mês quando o período tem um mês só.
 * @param budget Map(cod -> number[12]) do ano de referência
 * @param realized Map(cod -> number[12]) (só até o mês de referência)
 * @param budgetOf / realizedOf (year) => Map(cod -> number[12]): período que atravessa anos (no lugar de
 *   `budget` / `realized`)
 * @param progress periodProgress(...) / monthProgress(...)
 * @param labelOf (cod) => nome da categoria
 */
export function buildBudgetComparison({ budget, realized, budgetOf, realizedOf, progress, labelOf = (c) => c, natureOf = () => null }) {
  const budgetAt = budgetOf || (() => budget);
  const realizedAt = realizedOf || (() => realized);
  const ref = progress.until || { year: progress.year, monthIndex: progress.monthIndex };
  const m = ref.monthIndex;
  const periodMonths = progress.months || [{ year: ref.year, monthIndex: m, fraction: progress.fraction }];
  const years = [...new Set([ref.year, ...periodMonths.map((p) => p.year)])];
  const seriesOf = (map, cod) => map.get(cod) || zeros12();
  const cods = [...new Set(years.flatMap((y) => [...budgetAt(y).keys(), ...realizedAt(y).keys()]))];
  const natureFor = (cod) => natureOf(cod) || natureBySign(
    years.flatMap((y) => seriesOf(budgetAt(y), cod)),
    years.flatMap((y) => seriesOf(realizedAt(y), cod)),
  );
  // Orçado e realizado de cada categoria em cada mês do período (para os totais somarem mês a mês).
  const partsOf = new Map();
  const categories = cods.map((cod) => {
    const parts = periodMonths.map((p) => ({
      budget: seriesOf(budgetAt(p.year), cod)[p.monthIndex],
      realized: seriesOf(realizedAt(p.year), cod)[p.monthIndex],
      fraction: p.fraction,
    }));
    partsOf.set(cod, parts);
    const b = seriesOf(budgetAt(ref.year), cod);
    const r = seriesOf(realizedAt(ref.year), cod);
    const nature = natureFor(cod);
    return {
      cod,
      label: labelOf(cod) || cod,
      nature,
      month: comparePeriod(parts, { expense: nature === 'P' }),
      ytd: {
        budget: round2(sum(b.slice(0, m + 1))),
        realized: round2(sum(r.slice(0, m + 1))),
      },
      yearBudget: round2(sum(b)),
    };
  }).filter((c) => Math.abs(c.month.budget) > 0.005 || Math.abs(c.month.realized) > 0.005 || Math.abs(c.yearBudget) > 0.005);

  const totalOf = (filter, options) => {
    const list = categories.filter(filter);
    const parts = periodMonths.map((p, i) => ({
      budget: sum(list.map((c) => partsOf.get(c.cod)[i].budget)),
      realized: sum(list.map((c) => partsOf.get(c.cod)[i].realized)),
      fraction: p.fraction,
    }));
    return {
      ...comparePeriod(parts, options),
      ytdBudget: round2(sum(list.map((c) => c.ytd.budget))),
      ytdRealized: round2(sum(list.map((c) => c.ytd.realized))),
    };
  };
  const receitas = totalOf((c) => c.nature === 'R');
  const despesas = totalOf((c) => c.nature === 'P', { expense: true });
  const resultado = totalOf(() => true);
  // O resultado projetado é a soma das projeções (entradas no ritmo, saídas ao menos o orçado).
  resultado.projection = round2(receitas.projection + despesas.projection);

  const yearBudgetSeries = budgetAt(ref.year);
  const yearRealizedSeries = realizedAt(ref.year);
  const monthly = MONTH_INDEXES.map((i) => {
    const row = { monthIndex: i, receitas: { budget: 0, realized: 0 }, despesas: { budget: 0, realized: 0 }, resultado: { budget: 0, realized: 0 } };
    cods.forEach((cod) => {
      const b = seriesOf(yearBudgetSeries, cod)[i];
      const r = seriesOf(yearRealizedSeries, cod)[i];
      const bucket = natureFor(cod) === 'R' ? row.receitas : row.despesas;
      bucket.budget += b;
      bucket.realized += r;
      row.resultado.budget += b;
      row.resultado.realized += r;
    });
    const hasRealized = i <= m && progress.state !== 'future';
    ['receitas', 'despesas', 'resultado'].forEach((k) => {
      row[k].budget = round2(row[k].budget);
      row[k].realized = hasRealized ? round2(row[k].realized) : null;
    });
    return row;
  });

  return {
    categories,
    totals: { receitas, despesas, resultado },
    monthly,
  };
}

// ---------------- Resultado do DRE ----------------

/**
 * Linha de resultado do Simulador (Lucro Líquido; sem ele, o último totalizador) orçada × realizada e o
 * que mais mexeu nela no período:
 * - `monthly`: o ano de referência mês a mês ({ monthIndex, result: { budget, realized } }), com o
 *   realizado só até o mês de referência;
 * - `ranking` / `effect`: fechamento − orçado do período de cada conta do DRE (as linhas do ranking do
 *   Simulador) e da linha de resultado. O fechamento é o de `comparison`: mês fechado pelo realizado,
 *   mês em andamento pela projeção, mês futuro pelo orçado (sem desvio).
 * @param model dataset preparado (consolidado ou por departamento)
 * @param comparison buildBudgetComparison(...) com as mesmas séries
 */
export function buildResultTracking({ model, comparison, budgetOf, realizedOf, progress }) {
  const ref = progress.until || { year: progress.year, monthIndex: progress.monthIndex };
  const lineOf = (series, statement = computeStatement(model, series)) =>
    resultSeries(model, statement, computeBaseIndicatorSeries(model, series));
  const budget = lineOf(budgetOf(ref.year));
  const realized = lineOf(realizedOf(ref.year));
  const monthly = MONTH_INDEXES.map((m) => ({
    monthIndex: m,
    result: {
      budget: round2(budget.series[m]),
      realized: m <= ref.monthIndex && progress.state !== 'future' ? round2(realized.series[m]) : null,
    },
  }));

  // O DRE e os indicadores são somas das categorias, então valem sobre o desvio: o do período vai no
  // primeiro mês da série.
  const deviation = new Map(comparison.categories.map((c) => {
    const series = zeros12();
    series[0] = c.month.projection - c.month.budget;
    return [c.cod, series];
  }));
  const statement = computeStatement(model, deviation);
  const ranking = rankingLines(model)
    .map((id) => ({ nodeId: id, label: model.nodeById.get(id).descricao, delta: round2(statement.get(id)[0]) }))
    .filter((x) => Math.abs(x.delta) >= 0.5)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  return { label: budget.label, monthly, ranking, effect: round2(lineOf(deviation, statement).series[0]) };
}

// ---------------- Top impactos no orçamento ----------------

const impactOf = (key, label, total) => ({
  key,
  label,
  budget: total.budget,
  realized: total.realized,
  projection: total.projection,
  impact: round2(total.projection - total.budget),
});
const topByImpact = (list, limit) => list
  .filter((x) => Math.abs(x.impact) >= 0.5)
  .sort((a, b) => Math.abs(b.impact) - Math.abs(a.impact))
  .slice(0, limit);

/**
 * O que mais desviou do orçado no período, separado em receitas e despesas: por categoria (linhas de
 * `comparison`) e por departamento (entradas e saídas de cada um; só no cenário por departamento).
 * Impacto = fechamento − orçado no sinal do DRE (> 0 melhora o resultado), com o fechamento de
 * `comparison`: mês fechado pelo realizado, mês em andamento pela projeção, mês futuro pelo orçado.
 */
export function budgetImpactRanking({ categories = [], departments = [], limit = 10 }) {
  const byNature = (nature) => categories.filter((c) => c.nature === nature).map((c) => impactOf(c.cod, c.label, c.month));
  return {
    categories: {
      receitas: topByImpact(byNature('R'), limit),
      despesas: topByImpact(byNature('P'), limit),
    },
    departments: {
      receitas: topByImpact(departments.map((d) => impactOf(d.key, d.label, d.totals.receitas)), limit),
      despesas: topByImpact(departments.map((d) => impactOf(d.key, d.label, d.totals.despesas)), limit),
    },
  };
}

// ---------------- Indicadores do DRE ----------------

/**
 * Indicadores do Simulador (Receita Líquida, Lucro Bruto, EBITDA, Lucro Líquido, margens, MC, PE...) no
 * período: orçado, esperado até a data de corte, realizado e projeção do fechamento, cada um como
 * deriveIndicators(...). Os indicadores são somas das categorias (e fórmulas sobre as somas), então saem
 * das linhas de `comparison` — o mesmo realizado e a mesma projeção dos cards de Entradas/Saídas/Resultado.
 * `available`: chaves com configuração na empresa. Sem nenhuma (ex.: cenário por departamento), null.
 */
export function buildIndicatorTracking({ model, comparison }) {
  const available = INDICATOR_DEFS.filter((d) => isIndicatorAvailable(model, d)).map((d) => d.key);
  if (!available.length) return null;
  // O valor do período vai no primeiro mês da série, como no ranking do resultado.
  const derive = (field) => {
    const series = new Map(comparison.categories.map((c) => {
      const s = zeros12();
      s[0] = c.month[field];
      return [c.cod, s];
    }));
    return deriveIndicators(sumBaseIndicators(computeBaseIndicatorSeries(model, series), [0]));
  };
  return {
    available,
    budget: derive('budget'),
    expected: derive('expected'),
    realized: derive('realized'),
    projection: derive('projection'),
  };
}

// ---------------- Riscos e vitórias ----------------

// Mês em andamento: despesa é concentrada (aluguel, folha), então o ritmo dela não diz nada — só vira
// risco o que já passou do orçado do mês inteiro. A receita segue o ritmo, a partir de 25% do mês.
// Mês fechado: entradas e saídas comparadas com o orçado do mês.
export const MIN_FRACTION_FOR_PACE = 0.25;
const MIN_RELATIVE = 0.1;

/**
 * Riscos e vitórias por categoria no mês. Cada item: { cod, label, kind, amount, relative, message }.
 * `amount` = impacto em R$ (sempre positivo). Ordenados pelo impacto.
 */
export function categorySignals(categories, progress, { minAmount = 100, limit = 6 } = {}) {
  const risks = [];
  const wins = [];
  const paceReady = progress.state === 'past' || progress.fraction >= MIN_FRACTION_FOR_PACE;
  categories.forEach((c) => {
    const { budget, realized, expected, projection } = c.month;
    const revenue = c.nature === 'R';
    const base = { cod: c.cod, label: c.label, nature: c.nature, budget, realized, projection };

    // Sem orçado no mês: saída sem orçamento é risco; entrada sem orçamento é vitória.
    if (Math.abs(budget) < 0.005) {
      if (Math.abs(realized) >= minAmount) {
        (revenue ? wins : risks).push({ ...base, kind: 'sem_orcamento', amount: Math.abs(realized), relative: null });
      }
      return;
    }

    // O mês inteiro já foi superado (vale em qualquer dia do mês).
    const over = realized - budget; // sinal do DRE
    if (progress.state !== 'past') {
      if (!revenue && over < 0 && Math.abs(over) >= minAmount) {
        risks.push({ ...base, kind: 'estourado', amount: Math.abs(over), relative: Math.abs(over / budget) });
        return;
      }
      if (revenue && over >= 0 && realized >= minAmount) {
        wins.push({ ...base, kind: 'bateu', amount: Math.abs(over), relative: Math.abs(over / budget) });
        return;
      }
    }
    if (!paceReady || (!revenue && progress.state !== 'past')) return;

    // Ritmo (receita, mês em andamento) ou resultado final (mês fechado).
    const target = progress.state === 'past' ? budget : expected;
    const final = progress.state === 'past' ? realized : projection;
    const gap = realized - target;
    const relative = Math.abs(target) > 0.005 ? Math.abs((final - budget) / budget) : null;
    if (Math.abs(gap) < minAmount || (relative != null && relative < MIN_RELATIVE)) return;
    const kind = progress.state === 'past' ? 'fechou' : 'ritmo';
    (gap < 0 ? risks : wins).push({ ...base, kind, amount: Math.abs(gap), relative });
  });
  const byImpact = (a, b) => b.amount - a.amount;
  return { risks: risks.sort(byImpact).slice(0, limit), wins: wins.sort(byImpact).slice(0, limit) };
}

// ---------------- Metas ----------------

export const GOAL_STATUS = Object.freeze({
  ATINGIDA: 'atingida',
  NO_CAMINHO: 'no_caminho',
  ATENCAO: 'atencao',
  RISCO: 'risco',
  CRITICA: 'critica',
  NAO_ATINGIDA: 'nao_atingida',
  FUTURA: 'futura',
  SEM_DADOS: 'sem_dados',
});

export const GOAL_STATUS_ORDER = [
  GOAL_STATUS.CRITICA, GOAL_STATUS.RISCO, GOAL_STATUS.NAO_ATINGIDA, GOAL_STATUS.ATENCAO,
  GOAL_STATUS.NO_CAMINHO, GOAL_STATUS.ATINGIDA, GOAL_STATUS.FUTURA, GOAL_STATUS.SEM_DADOS,
];

/**
 * Natureza da meta, que diz o que é "bom", independente do sinal digitado na meta:
 * - despesa: o valor gasto (absoluto) abaixo da meta é bom, acima é ruim;
 * - receita: o realizado acima da meta é bom, abaixo é ruim;
 * - resultado (lucro, EBITDA, margens, totalizadores): no sinal, maior é melhor.
 */
export const GOAL_KIND = Object.freeze({ DESPESA: 'despesa', RECEITA: 'receita', RESULTADO: 'resultado' });

const EXPENSE_METRICS = new Set(['basica:SAIDAS_REALIZADAS', 'indicador:IMPOSTOS', 'indicador:CUSTOS', 'indicador:CUSTOS_PCT', 'indicador:PE']);
const REVENUE_METRICS = new Set(['basica:ENTRADAS_REALIZADAS', 'basica:VENDAS_TOTAL', 'indicador:RB', 'indicador:RL']);

/** Natureza pela métrica (sem olhar os dados): categoria e conta pelo sinal da meta. */
export const defaultGoalKind = (goal) => {
  const id = `${goal.metricType}:${goal.metricKey}`;
  if (EXPENSE_METRICS.has(id)) return GOAL_KIND.DESPESA;
  if (REVENUE_METRICS.has(id)) return GOAL_KIND.RECEITA;
  if (goal.metricType === METRIC_TYPE.CATEGORIA || goal.metricType === METRIC_TYPE.NO) {
    return (Number(goal.value) || 0) < 0 ? GOAL_KIND.DESPESA : GOAL_KIND.RECEITA;
  }
  return GOAL_KIND.RESULTADO;
};

/**
 * Realizado de uma meta no período dela (até a data de corte), a partir de:
 * - `consolidated`: { model, seriesByYear(year) => Map(cod -> [12]), statementByYear?(year) } (DRE:
 *   categoria, conta, totalizador, indicador);
 * - `byDepartment`: { seriesByYear(year, dept) } (títulos realizados; metas de departamento);
 * - `basics`: { [metricKey]: number } (já somado no período da meta).
 * Retorna null quando não há base para calcular.
 */
export function goalRealized(goal, { consolidated, byDepartment, basics, until }) {
  const months = monthsBetween(goal.periodStart, goal.periodEnd)
    .filter((p) => !until || p.year < until.year || (p.year === until.year && p.monthIndex <= until.monthIndex));
  const byYear = new Map();
  months.forEach((p) => {
    if (!byYear.has(p.year)) byYear.set(p.year, []);
    byYear.get(p.year).push(p.monthIndex);
  });
  const type = goal.metricType;
  const key = goal.metricKey;

  if (type === METRIC_TYPE.BASICA) {
    const v = basics?.[`${goal.id}`];
    return v == null ? null : v;
  }

  if (goal.departmentKey) {
    if (!byDepartment || type !== METRIC_TYPE.CATEGORIA) return null;
    let total = 0;
    byYear.forEach((idx, year) => {
      const series = byDepartment.seriesByYear(year, goal.departmentKey).get(key);
      if (series) total += idx.reduce((acc, m) => acc + series[m], 0);
    });
    return total;
  }

  if (!consolidated?.model) return null;
  const { model } = consolidated;
  if ((type === METRIC_TYPE.NO || type === METRIC_TYPE.TOTALIZADOR) && !model.nodeById.has(key)) return null;
  if (type === METRIC_TYPE.INDICADOR) {
    if (!INDICATOR_DEF_BY_KEY[key]) return null;
    const sums = {};
    byYear.forEach((idx, year) => {
      const s = sumBaseIndicators(computeBaseIndicatorSeries(model, consolidated.seriesByYear(year)), idx);
      Object.entries(s).forEach(([k, v]) => { sums[k] = (sums[k] || 0) + v; });
    });
    return deriveIndicators(sums)[key];
  }
  let total = 0;
  byYear.forEach((idx, year) => {
    const series = type === METRIC_TYPE.CATEGORIA
      ? consolidated.seriesByYear(year).get(key)
      : (consolidated.statementByYear?.(year) || computeStatement(model, consolidated.seriesByYear(year))).get(key);
    if (series) total += idx.reduce((acc, m) => acc + series[m], 0);
  });
  return total;
}

/**
 * Natureza da meta de categoria ou conta pela natureza da própria categoria (cadastro do ERP; a conta do
 * DRE, pela das categorias dela) — vale mesmo quando a meta foi digitada com o sinal trocado. Só sem
 * natureza no ERP: pelo sinal do realizado no ano e, sem realizado, pelo sinal da meta.
 * `consolidated.natureOf` / `byDepartment.natureOf`: (cod) => 'R' | 'P' | null.
 */
export function goalKind(goal, { consolidated, byDepartment, until } = {}) {
  const base = defaultGoalKind(goal);
  if (goal.metricType !== METRIC_TYPE.CATEGORIA && goal.metricType !== METRIC_TYPE.NO) return base;
  const fromNature = (nature) => (nature === 'R' ? GOAL_KIND.RECEITA : nature === 'P' ? GOAL_KIND.DESPESA : null);
  if (goal.metricType === METRIC_TYPE.CATEGORIA) {
    const source = goal.departmentKey ? byDepartment : consolidated;
    const kind = fromNature(source?.natureOf?.(goal.metricKey));
    if (kind) return kind;
  } else if (consolidated?.natureOf && consolidated?.model?.nodeById.has(goal.metricKey)) {
    // Conta do DRE: a natureza da maioria das categorias dela.
    const natures = consolidated.model.nodeById.get(goal.metricKey).allCats.map((cod) => consolidated.natureOf(cod)).filter(Boolean);
    if (natures.length) {
      const revenue = natures.filter((n) => n === 'R').length;
      return revenue * 2 >= natures.length ? GOAL_KIND.RECEITA : GOAL_KIND.DESPESA;
    }
  }
  const years = [...new Set(monthsBetween(goal.periodStart, goal.periodEnd).map((p) => p.year))];
  let total = 0;
  years.forEach((year) => {
    let series = null;
    if (goal.departmentKey) series = byDepartment?.seriesByYear(year, goal.departmentKey).get(goal.metricKey);
    else if (goal.metricType === METRIC_TYPE.CATEGORIA) series = consolidated?.seriesByYear(year).get(goal.metricKey);
    else if (consolidated?.model?.nodeById.has(goal.metricKey)) {
      series = (consolidated.statementByYear?.(year) || computeStatement(consolidated.model, consolidated.seriesByYear(year))).get(goal.metricKey);
    }
    const last = until && year === until.year ? until.monthIndex : 11;
    if (series && (!until || year <= until.year)) total += series.slice(0, last + 1).reduce((a, b) => a + b, 0);
  });
  if (Math.abs(total) > 0.005) return total > 0 ? GOAL_KIND.RECEITA : GOAL_KIND.DESPESA;
  return base;
}

/**
 * Situação de uma meta na data de corte: esperado (proporcional aos dias do período), progresso,
 * projeção e status, conforme a natureza (`kind`; sem ela, pela métrica e pelo sinal da meta).
 * Despesa: compara valores absolutos (gastar menos que a meta é bom); como na despesa do orçamento, o
 * ritmo dos dias não diz muito — acima do proporcional é atenção e crítico é estourar a meta do período.
 */
export function evaluateGoal(goal, realized, asOf, { kind } = {}) {
  const goalKindValue = kind || defaultGoalKind(goal);
  const expense = goalKindValue === GOAL_KIND.DESPESA;
  const percent = goal.format === 'percent';
  const fraction = periodFraction(goal.periodStart, goal.periodEnd, asOf);
  const rawValue = Number(goal.value) || 0;
  // Despesa: valor gasto e meta em absoluto. Receita: meta em absoluto. Resultado: no sinal.
  const v = expense || goalKindValue === GOAL_KIND.RECEITA ? Math.abs(rawValue) : rawValue;
  const base = { goal, kind: goalKindValue, limit: expense, value: v, realized, fraction };

  if (fraction <= 0) return { ...base, status: GOAL_STATUS.FUTURA, expected: 0, projection: null, progressPct: null };
  if (realized == null) return { ...base, status: GOAL_STATUS.SEM_DADOS, expected: null, projection: null, progressPct: null };

  const r = expense ? Math.abs(realized) : realized;
  const better = (a, b) => (expense ? a <= b : a >= b);
  const ended = fraction >= 1;
  const expected = percent ? v : v * fraction;
  const projection = percent ? r : expense ? null : (fraction > 0 ? r / fraction : r);
  const progressPct = v ? (r / v) * 100 : null;

  let status;
  if (ended) {
    status = better(r, v) ? GOAL_STATUS.ATINGIDA : GOAL_STATUS.NAO_ATINGIDA;
  } else if (expense) {
    if (!better(r, v)) status = GOAL_STATUS.CRITICA; // já passou da meta do período inteiro
    else status = percent || better(r, expected) ? GOAL_STATUS.NO_CAMINHO : GOAL_STATUS.ATENCAO;
  } else if (!percent && v > 0 && r >= v) {
    status = GOAL_STATUS.ATINGIDA; // já bateu a meta do período inteiro
  } else if (better(r, expected)) {
    status = GOAL_STATUS.NO_CAMINHO;
  } else {
    const gap = Math.abs(expected) > 0.005 ? Math.abs((r - expected) / expected) : 1;
    status = gap < 0.1 ? GOAL_STATUS.ATENCAO : gap < 0.25 ? GOAL_STATUS.RISCO : GOAL_STATUS.CRITICA;
  }
  return { ...base, realized: r, expected: round2(expected), projection: projection == null ? null : round2(projection), progressPct, status };
}

/** Placar das metas por status. */
export const goalScoreboard = (evaluations) => {
  const out = Object.fromEntries(Object.values(GOAL_STATUS).map((s) => [s, 0]));
  evaluations.forEach((e) => { out[e.status] += 1; });
  return out;
};

export const sortGoalEvaluations = (evaluations) => [...evaluations].sort((a, b) =>
  GOAL_STATUS_ORDER.indexOf(a.status) - GOAL_STATUS_ORDER.indexOf(b.status)
  || String(a.goal.metricLabel).localeCompare(String(b.goal.metricLabel), 'pt-BR'));
